import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Loader2 } from 'lucide-react';
import { customFieldsAPI } from '../lib/api';
import { toast } from 'sonner';

const GROUPS = [
  { value: 'personal', label: 'Personal Information' },
  { value: 'contact', label: 'Contact & Address' },
  { value: 'immigration', label: 'Immigration' },
  { value: 'biographic', label: 'Biographic Information' },
  { value: 'employment', label: 'Employment' },
  { value: 'family', label: 'Family' },
  { value: 'travel', label: 'Travel' },
];

export default function AddCustomFieldDialog({ organizationId, onFieldAdded }) {
  const [open, setOpen] = useState(false);
  const [fieldName, setFieldName] = useState('');
  const [group, setGroup] = useState('personal');
  const [isStandard, setIsStandard] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!fieldName.trim()) {
      toast.error('Field name is required');
      return;
    }
    setLoading(true);
    try {
      await customFieldsAPI.create({
        field_name: fieldName.trim(),
        group,
        is_standard: isStandard,
        organization_id: organizationId,
      });
      toast.success(`Custom field "${fieldName}" created`);
      setFieldName('');
      setGroup('personal');
      setIsStandard(false);
      setOpen(false);
      onFieldAdded?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create custom field';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="add-custom-field-btn">
          <Plus className="w-4 h-4 mr-1.5" />
          Add Custom Field
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="add-custom-field-dialog">
        <DialogHeader>
          <DialogTitle>Add Custom Field</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="field-name">Field Name</Label>
            <Input
              id="field-name"
              placeholder="e.g. License Number"
              value={fieldName}
              onChange={e => setFieldName(e.target.value)}
              data-testid="custom-field-name-input"
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
          </div>
          <div className="space-y-2">
            <Label>Section</Label>
            <Select value={group} onValueChange={setGroup}>
              <SelectTrigger data-testid="custom-field-group-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROUPS.map(g => (
                  <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="is-standard" className="text-sm">Standard for organization</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Applies to all cases in this org</p>
            </div>
            <Switch
              id="is-standard"
              checked={isStandard}
              onCheckedChange={setIsStandard}
              data-testid="custom-field-standard-switch"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="custom-field-save-btn">
            {loading && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Add Field
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
